import React, { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Rocket, ShieldCheck, RefreshCw, BarChart2, Users, Package,
    CheckCircle, ArrowRight, Zap, Star, ChevronDown
} from 'lucide-react';
import LanguageSwitcher from '../components/LanguageSwitcher';

/* ─── CONFIG ─────────────────────────────────────────────── */
const WHATSAPP_NUMBER = '212600000000'; // ← بدل برقمك

const plans = [
    {
        id: 'starter',
        name: 'Starter',
        price: '70',
        stores: 1,
        badge: null,
        gradient: 'from-blue-500 to-indigo-600',
        features: [
            '1 boutique / brand',
            'Gestion des commandes',
            'Sync Google Sheets',
            'Équipe illimitée',
            'Intégration livraison',
            'Support par email',
        ],
    },
    {
        id: 'growth',
        name: 'Growth',
        price: '199',
        stores: 5,
        badge: 'Populaire',
        gradient: 'from-violet-500 to-purple-700',
        features: [
            "Jusqu'à 5 boutiques",
            'Gestion des commandes',
            'Sync Google Sheets',
            'Équipe illimitée',
            'Intégration livraison',
            'Support prioritaire',
        ],
    },
];

const features = [
    {
        icon: BarChart2,
        title: 'Dashboard en temps réel',
        desc: "Suivez vos KPIs, taux de confirmation et revenus en un coup d'œil.",
    },
    {
        icon: RefreshCw,
        title: 'Sync Google Sheets',
        desc: 'Importation automatique des commandes depuis votre feuille Google.',
    },
    {
        icon: Users,
        title: "Gestion d'équipe",
        desc: 'Agents de confirmation, rôles et performances — tout centralisé.',
    },
    {
        icon: Package,
        title: 'Multi-boutique isolée',
        desc: 'Chaque brand est 100 % isolée. Aucune fuite de données entre clients.',
    },
    {
        icon: Zap,
        title: 'Expédition en 1 clic',
        desc: 'Envoyez vos commandes aux transporteurs directement depuis le panel.',
    },
    {
        icon: ShieldCheck,
        title: 'Sécurité maximale',
        desc: 'JWT, cookies sécurisés et isolation au niveau base de données.',
    },
];

/* ─── HELPERS ─────────────────────────────────────────────── */
function openWhatsApp(planName, price) {
    const msg = `Bonjour, je suis intéressé(e) par le plan ${planName} à ${price} DH/mois. Pouvez-vous m'aider ?`;
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(msg)}`, '_blank');
}

/* ─── NAVBAR ──────────────────────────────────────────────── */
function Navbar({ onLogin }) {
    return (
        <header className="fixed top-0 left-0 right-0 z-50 bg-[#0f172a]/85 backdrop-blur-xl border-b border-white/[0.07]">
            <div className="max-w-[1200px] mx-auto px-6 h-16 flex items-center justify-between">
                {/* Logo */}
                <div className="flex items-center gap-2.5">
                    <div className="w-9 h-9 bg-gradient-to-br from-indigo-500 to-blue-500 rounded-[10px] flex items-center justify-center shadow-lg shadow-indigo-500/40">
                        <Rocket size={18} className="text-white" />
                    </div>
                    <span className="text-[1.15rem] font-extrabold text-gray-50 tracking-tight">Adminify</span>
                </div>

                {/* Right */}
                <div className="flex items-center gap-4">
                    <div className="opacity-75 hidden sm:block">
                        <LanguageSwitcher />
                    </div>
                    <button
                        onClick={onLogin}
                        className="flex items-center gap-1.5 px-4 py-2 rounded-[10px] border border-white/15 bg-white/5 text-gray-300 text-sm font-semibold hover:bg-white/[0.12] hover:border-white/30 hover:text-white transition-all"
                    >
                        Se connecter <ArrowRight size={15} />
                    </button>
                </div>
            </div>
        </header>
    );
}

/* ─── HERO ────────────────────────────────────────────────── */
function Hero({ onScrollToPricing }) {
    return (
        <section className="relative min-h-screen flex flex-col items-center justify-center px-6 pt-[100px] pb-20 overflow-hidden">
            {/* Glows */}
            <div className="absolute w-[600px] h-[600px] rounded-full bg-indigo-500/30 blur-[100px] -top-36 -right-24 pointer-events-none" />
            <div className="absolute w-[500px] h-[500px] rounded-full bg-blue-500/20 blur-[100px] -bottom-24 -left-24 pointer-events-none" />
            <div className="absolute w-[300px] h-[300px] rounded-full bg-violet-500/15 blur-[80px] top-[40%] left-[40%] pointer-events-none" />

            {/* Content */}
            <div className="relative z-10 text-center max-w-[760px]">
                {/* Pill */}
                <div className="inline-flex items-center gap-1.5 bg-indigo-500/15 border border-indigo-500/35 text-indigo-300 text-[0.78rem] font-semibold uppercase tracking-wider px-4 py-1.5 rounded-full mb-6">
                    <Star size={12} className="text-amber-400" />
                    <span>Plateforme SaaS E-commerce Maroc</span>
                </div>

                {/* Title */}
                <h1 className="text-4xl sm:text-5xl md:text-[3.75rem] font-black leading-[1.12] tracking-tight text-gray-50 mb-5">
                    Gérez votre boutique
                    <br />
                    <span className="bg-gradient-to-r from-indigo-400 to-sky-400 bg-clip-text text-transparent">en toute simplicité</span>
                </h1>

                {/* Subtitle */}
                <p className="text-[1.1rem] text-slate-400 leading-relaxed mb-9 max-w-[560px] mx-auto">
                    La solution complète pour les marques marocaines e-commerce. Commandes,
                    équipe, livraison et analytics — tout en un seul panel.
                </p>

                {/* CTAs */}
                <div className="flex items-center justify-center flex-wrap gap-3.5 mb-12">
                    <button
                        onClick={onScrollToPricing}
                        className="flex items-center gap-2 px-8 py-3.5 bg-gradient-to-br from-indigo-500 to-indigo-700 text-white font-bold text-[0.95rem] rounded-xl shadow-lg shadow-indigo-500/40 hover:-translate-y-0.5 hover:shadow-xl hover:shadow-indigo-500/50 transition-all"
                    >
                        Voir les tarifs <ChevronDown size={18} />
                    </button>
                    <button
                        onClick={onScrollToPricing}
                        className="px-8 py-3.5 bg-white/[0.06] text-slate-300 font-semibold text-[0.95rem] rounded-xl border border-white/[0.12] hover:bg-white/10 hover:text-white transition-all"
                    >
                        Démarrer maintenant
                    </button>
                </div>

                {/* Stats */}
                <div className="flex justify-center gap-10 flex-wrap">
                    {[
                        { val: '100%', label: 'Isolation des données' },
                        { val: '5s', label: 'Sync automatique' },
                        { val: '24/7', label: 'Uptime garanti' },
                    ].map((s) => (
                        <div key={s.label} className="flex flex-col items-center gap-0.5">
                            <span className="text-2xl font-extrabold text-gray-50 tracking-tight">{s.val}</span>
                            <span className="text-[0.72rem] text-slate-500 uppercase tracking-widest font-semibold">{s.label}</span>
                        </div>
                    ))}
                </div>
            </div>

            {/* Scroll hint */}
            <div
                onClick={onScrollToPricing}
                className="absolute bottom-8 left-1/2 -translate-x-1/2 cursor-pointer opacity-40 hover:opacity-80 transition-opacity"
            >
                <ChevronDown size={24} className="text-slate-400 animate-bounce" />
            </div>
        </section>
    );
}

/* ─── FEATURES ────────────────────────────────────────────── */
function Features() {
    return (
        <section className="py-20 px-6 bg-[#f8fafc]">
            <div className="max-w-[1200px] mx-auto">
                {/* Header */}
                <div className="text-center mb-14">
                    <span className="inline-block bg-indigo-500/10 text-indigo-600 text-xs font-bold uppercase tracking-widest px-3.5 py-1 rounded-full border border-indigo-500/20 mb-4">
                        Fonctionnalités
                    </span>
                    <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900 mb-3">
                        Tout ce dont vous avez besoin
                    </h2>
                    <p className="text-slate-500 max-w-[520px] mx-auto leading-relaxed">
                        Une plateforme pensée pour les opérations e-commerce marocaines.
                    </p>
                </div>

                {/* Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {features.map((f) => (
                        <div
                            key={f.title}
                            className="bg-white rounded-2xl p-7 border border-slate-200 shadow-sm hover:-translate-y-1 hover:shadow-lg hover:shadow-indigo-500/10 hover:border-indigo-200 transition-all duration-300"
                        >
                            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-indigo-50 to-indigo-100 text-indigo-500 flex items-center justify-center mb-4">
                                <f.icon size={22} />
                            </div>
                            <h3 className="text-base font-bold text-slate-900 mb-2">{f.title}</h3>
                            <p className="text-sm text-slate-500 leading-relaxed">{f.desc}</p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

/* ─── PRICING ─────────────────────────────────────────────── */
// function Pricing({ pricingRef }) {
//     return (
//         <section ref={pricingRef} className="py-20 px-6 bg-slate-100">
//             <div className="max-w-[1200px] mx-auto">
//                 {/* Header */}
//                 <div className="text-center mb-14">
//                     <span className="inline-block bg-violet-500/10 text-violet-600 text-xs font-bold uppercase tracking-widest px-3.5 py-1 rounded-full border border-violet-500/20 mb-4">
//                         Tarifs
//                     </span>
//                     <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900 mb-3">
//                         Un plan pour chaque besoin
//                     </h2>
//                     <p className="text-slate-500 max-w-[520px] mx-auto leading-relaxed">
//                         Commencez avec une boutique, évoluez vers plusieurs.
//                     </p>
//                 </div>

//                 {/* Plans */}
//                 <div className="grid grid-cols-1 md:grid-cols-2 gap-7 max-w-[780px] mx-auto">
//                     {plans.map((plan) => (
//                         <div
//                             key={plan.id}
//                             className={`relative bg-white rounded-[20px] overflow-hidden border-2 shadow-md hover:-translate-y-1 hover:shadow-xl transition-all duration-300 ${plan.badge
//                                     ? 'border-violet-500 shadow-violet-500/15'
//                                     : 'border-slate-200'
//                                 }`}
//                         >
//                             {/* Badge */}
//                             {plan.badge && (
//                                 <div className="absolute top-3.5 right-3.5 z-10 bg-gradient-to-r from-amber-400 to-red-500 text-white text-[0.7rem] font-extrabold uppercase tracking-wider px-3 py-1 rounded-full shadow-md shadow-red-500/30">
//                                     {plan.badge}
//                                 </div>
//                             )}

//                             {/* Header */}
//                             <div className={`p-7 pb-5 text-white bg-gradient-to-br ${plan.gradient}`}>
//                                 <span className="text-[0.85rem] font-bold uppercase tracking-widest opacity-85 block mb-2">
//                                     {plan.name}
//                                 </span>
//                                 <div className="flex items-baseline gap-0.5 mb-0.5">
//                                     <span className="text-lg font-bold opacity-80 mt-1">DH</span>
//                                     <span className="text-5xl font-black tracking-tighter leading-none">{plan.price}</span>
//                                 </div>
//                                 <span className="text-[0.82rem] opacity-75 block mb-3">par mois</span>
//                                 <div className="inline-flex items-center gap-1.5 bg-white/[0.18] px-3 py-1 rounded-full text-[0.78rem] font-semibold">
//                                     <Package size={14} />
//                                     {plan.stores === 1 ? '1 boutique incluse' : `Jusqu'à ${plan.stores} boutiques`}
//                                 </div>
//                             </div>

//                             {/* Body */}
//                             <div className="p-7 pt-6">
//                                 <ul className="space-y-2.5 mb-6">
//                                     {plan.features.map((f) => (
//                                         <li key={f} className="flex items-center gap-2.5 text-sm text-gray-700 font-medium">
//                                             <CheckCircle size={16} className="text-emerald-500 flex-shrink-0" />
//                                             <span>{f}</span>
//                                         </li>
//                                     ))}
//                                 </ul>
//                                 <button
//                                     onClick={() => openWhatsApp(plan.name, plan.price)}
//                                     className={`w-full flex items-center justify-center gap-2 py-3.5 text-white font-bold text-[0.9rem] rounded-xl bg-gradient-to-r ${plan.gradient} shadow-lg hover:-translate-y-0.5 hover:shadow-xl transition-all`}
//                                 >
//                                     Commencer — {plan.price} DH <ArrowRight size={16} />
//                                 </button>
//                             </div>
//                         </div>
//                     ))}
//                 </div>

//                 <p className="text-center text-slate-400 text-sm mt-8">
//                     Pas de frais cachés. Résiliation à tout moment. Support inclus.
//                 </p>
//             </div>
//         </section>
//     );
// }

/* ─── FOOTER ──────────────────────────────────────────────── */
function Footer({ onLogin }) {
    return (
        <footer className="bg-[#0f172a] border-t border-white/[0.06] py-8 px-6">
            <div className="max-w-[1200px] mx-auto flex items-center justify-between flex-wrap gap-4 sm:flex-row flex-col text-center sm:text-left">
                <div className="flex items-center gap-2">
                    <div className="w-7 h-7 bg-gradient-to-br from-indigo-500 to-blue-500 rounded-lg flex items-center justify-center">
                        <Rocket size={13} className="text-white" />
                    </div>
                    <span className="text-sm font-extrabold text-gray-50">Adminify</span>
                </div>
                <p className="text-xs text-slate-600 m-0">
                    © {new Date().getFullYear()} Adminify. Tous droits réservés.
                </p>
                <button
                    onClick={onLogin}
                    className="bg-transparent border-none text-indigo-500 text-sm font-semibold cursor-pointer hover:text-indigo-400 transition-colors"
                >
                    Se connecter →
                </button>
            </div>
        </footer>
    );
}

/* ─── PAGE ────────────────────────────────────────────────── */
const LandingPage = () => {
    const navigate = useNavigate();
    const pricingRef = useRef(null);

    const scrollToPricing = () => {
        pricingRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    return (
        <div className="min-h-screen bg-[#0f172a] text-gray-50 overflow-x-hidden">
            <Navbar onLogin={() => navigate('/login')} />
            <Hero onScrollToPricing={scrollToPricing} />
            <Features />
            {/* <Pricing pricingRef={pricingRef} /> */}
            <Footer onLogin={() => navigate('/login')} />
        </div>
    );
};

export default LandingPage;
